import { useEffect, useState } from "react";
import { api, fmtEur, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Ticket, Trash, PencilSimple, UserCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  name: "", description: "", total_washes: 8, price_eur: 200, wash_type: "", valid_days: 365,
};

export default function VouchersPage() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  // Assignment modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assign, setAssign] = useState({ voucher_id: "", customer_id: "" });

  const load = async () => {
    const [v, c, cu] = await Promise.all([
      api.get("/vouchers"),
      api.get("/catalog").catch(() => ({ data: [] })),
      api.get("/customers").catch(() => ({ data: [] })),
    ]);
    setItems(v.data); setCatalog(c.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ name: v.name, description: v.description || "", total_washes: v.total_washes,
              price_eur: v.price_eur, wash_type: v.wash_type || "", valid_days: v.valid_days });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre obligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...form, total_washes: Number(form.total_washes), price_eur: Number(form.price_eur), valid_days: Number(form.valid_days) };
      if (editingId) await api.put(`/vouchers/${editingId}`, payload);
      else await api.post("/vouchers", payload);
      toast.success(editingId ? "Bono actualizado" : "Bono creado");
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar bono?")) return;
    try { await api.delete(`/vouchers/${id}`); toast.success("Bono eliminado"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openAssign = (v) => { setAssign({ voucher_id: v.id, customer_id: "" }); setAssignOpen(true); };
  const submitAssign = async () => {
    if (!assign.customer_id) { toast.error("Selecciona cliente"); return; }
    try {
      await api.post("/customer-vouchers/assign", assign);
      toast.success("Bono asignado correctamente");
      setAssignOpen(false);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const perWashPrice = (v) => v.total_washes > 0 ? v.price_eur / v.total_washes : 0;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Programa de fidelización</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Bonos prepagados</h1>
          <p className="text-xs text-gray-500 mt-1">Crea packs cerrados (ej. 8 servicios por 200€) y véndelos a tus clientes. Al cobrar, resta saldo sin tocar la caja.</p>
        </div>
        <Button onClick={startCreate} className="ag-btn-gold rounded-sm" data-testid="vouchers-new-btn">
          <Plus className="mr-1.5" size={15} /> Nuevo bono
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-gray-300 rounded-md text-gray-500" data-testid="vouchers-empty">
            <Ticket size={36} weight="duotone" className="mx-auto" />
            <p className="mt-3 text-sm">Aún no tienes bonos creados</p>
          </div>
        )}
        {items.map((v) => (
          <div key={v.id} className="bg-white border border-gray-200 rounded-md p-5 relative" data-testid={`voucher-card-${v.id}`}>
            <div className="absolute top-3 right-3 flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => startEdit(v)} title="Editar"><PencilSimple size={14} /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(v.id)} title="Eliminar"><Trash size={14} className="text-red-600" /></Button>
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
              <Ticket size={11} weight="fill" /> Pack
            </div>
            <h3 className="font-display font-bold text-lg mt-1 tracking-tight pr-12">{v.name}</h3>
            {v.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{v.description}</p>}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="border border-gray-200 rounded-sm p-2 text-center min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Servicios</div>
                <div className="font-display font-bold text-base sm:text-lg tabular-nums">{v.total_washes}</div>
              </div>
              <div className="border border-gray-200 rounded-sm p-2 text-center min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Precio</div>
                <div className="font-display font-bold text-sm sm:text-base tabular-nums">{fmtEur(v.price_eur)}</div>
              </div>
              <div className="border border-gray-200 rounded-sm p-2 text-center bg-amber-50 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-amber-700">P/u</div>
                <div className="font-display font-bold text-sm sm:text-base tabular-nums text-amber-800">{fmtEur(perWashPrice(v))}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1"><Clock size={11} /> Validez: {v.valid_days} días</span>
              {v.wash_type && <span className="text-amber-700">Solo: {v.wash_type}</span>}
            </div>
            <Button onClick={() => openAssign(v)} className="w-full mt-3 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-9 text-xs" data-testid={`voucher-assign-${v.id}`}>
              <UserCircle size={13} className="mr-1.5" /> Asignar a cliente
            </Button>
          </div>
        ))}
      </div>

      {/* Create / edit voucher modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="voucher-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">{editingId ? "Editar bono" : "Nuevo bono"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" placeholder="Pack 8 servicios Premium" data-testid="voucher-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" placeholder="Ahorra un 20% sobre precio individual" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Servicios *</Label>
                <Input type="number" min={1} max={100} value={form.total_washes} onChange={(e) => setForm({ ...form, total_washes: e.target.value })} className="mt-1 rounded-sm tabular-nums" data-testid="voucher-washes" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Precio €</Label>
                <Input type="number" min={0} step="0.01" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className="mt-1 rounded-sm tabular-nums" data-testid="voucher-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Validez (días)</Label>
                <Input type="number" min={1} value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} className="mt-1 rounded-sm tabular-nums" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Restringir a un tipo de servicio</Label>
              <Select value={form.wash_type || "__any__"} onValueChange={(v) => setForm({ ...form, wash_type: v === "__any__" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">— Cualquier servicio</SelectItem>
                  {catalog.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.name} className="ag-btn-gold rounded-sm" data-testid="voucher-submit">
              {busy ? "Guardando…" : editingId ? "Actualizar" : "Crear bono"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign modal */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="voucher-assign-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tighter">Asignar bono a cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
              <Select value={assign.customer_id} onValueChange={(v) => setAssign({ ...assign, customer_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="voucher-assign-customer"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.main_license_plate ? ` · ${c.main_license_plate}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitAssign} disabled={!assign.customer_id} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="voucher-assign-submit">
              Asignar bono
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
